import React, { useState } from "react";
import {
  Box, Typography, Card, CardContent, Chip, Collapse, IconButton, Skeleton
} from "@mui/material";
import { DangerTriangle, Activity, CheckCircle, CheckHexagon } from "@mynaui/icons-react";
import HistoryIcon from "@mui/icons-material/History";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// ── Static config ────────────────────────────────────────────────
const STEP_META = [
  {
    stepNumber: 1,
    title: "Anomaly Trigger",
    subtitle: "Sensor Data Analysis & ML Model Prediction",
    status: "Escalated",
    statusColor: "#D97706",
    confidence: 89,
    Icon: DangerTriangle,
  },
  {
    stepNumber: 2,
    title: "Contextual Validation",
    subtitle: "Verify Asset Failures & Maintenance History",
    status: "Validated",
    statusColor: "#7C3AED",
    confidence: 94,
    Icon: CheckCircle,
  },
  {
    stepNumber: 3,
    title: "Sensor Knowledge Lookup & Work Order Planning",
    subtitle: "Sensor Knowledge Lookup & Work Order Planning",
    status: "Awaiting Approval",
    statusColor: "#D97706",
    confidence: 91,
    Icon: Activity,
  },
  {
    stepNumber: 4,
    title: "Work Order Generation",
    subtitle: "Work Order Creation & Technician Assignment",
    status: "Approved",
    statusColor: "#16A34A",
    confidence: 100,
    Icon: CheckHexagon,
  },
];

const STATIC_DATA_INPUTS = [
  "Air & process temperature sensor time-series (24h)",
  "Rotational speed and torque readings",
  "Tool wear progression data",
  "Historical failure records",
];

// ── StepCard ─────────────────────────────────────────────────────
const StepCard = ({ meta, description, isLast }) => {
  const [expanded, setExpanded] = useState(true);
  const { Icon } = meta;

  return (
    <Box sx={{ display: "flex", gap: 2, position: "relative" }}>
      {/* Pink vertical line */}
      {!isLast && (
        <Box
          sx={{
            position: "absolute",
            left: 9,
            top: 24,
            bottom: -16,
            width: 3,
            bgcolor: "#F9A8D4",
            zIndex: 0,
          }}
        />
      )}

      {/* Pink dot */}
      <Box
        sx={{
          width: 20,
          height: 20,
          borderRadius: "50%",
          bgcolor: "#F472B6",
          flexShrink: 0,
          mt: 0.5,
          zIndex: 1,
        }}
      />

      {/* Content */}
      <Box sx={{ flex: 1, pb: isLast ? 0 : 3 }}>
        <Typography sx={{ fontSize: "0.75rem", color: "#6B7280", mb: 0.75 }}>
          Step {meta.stepNumber} : {meta.title}
        </Typography>

        <Card
          sx={{
            borderRadius: 1,
            border: "1px solid #E5E7EB",
            boxShadow: "none",
            mb: 0,
          }}
        >
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Box
              onClick={() => setExpanded(!expanded)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    bgcolor: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon sx={{ fontSize: 15, color: meta.statusColor }} />
                </Box>
                <Typography
                  sx={{ fontWeight: 600, fontSize: "0.9375rem", color: "#1B2A3D" }}
                >
                  {meta.subtitle}
                </Typography>
              </Box>
              <IconButton size="small">
                <ExpandMoreIcon
                  sx={{
                    fontSize: 20,
                    transform: expanded ? "rotate(180deg)" : "none",
                    transition: "transform 0.2s",
                  }}
                />
              </IconButton>
            </Box>

            <Collapse in={expanded}>
              <Typography
                sx={{
                  fontSize: "0.8125rem",
                  color: "#4B5563",
                  lineHeight: 1.7,
                  mt: 1.5,
                }}
              >
                {description ?? <Skeleton variant="text" width="90%" />}
              </Typography>
            </Collapse>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

// ── LogsAndEvents ─────────────────────────────────────────────────
const LogsAndEvents = ({ flowInsights }) => {
  const getStepDescription = (stepNumber) => {
    if (!flowInsights) return null;
    return flowInsights[`step${stepNumber}`] ?? null;
  };

  return (
    <Card sx={{ borderRadius: 1, mb: 3 }}>
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>

        {/* Header — always visible */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
          <HistoryIcon sx={{ fontSize: 20, color: "text.secondary" }} />
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "text.primary" }}>
            Logs and Events
          </Typography>
        </Box>

        {/* Empty state */}
        {!flowInsights ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 80,
              border: "1px dashed #E2E8F0",
              borderRadius: "8px",
            }}
          >
            <Typography sx={{ fontSize: "0.875rem", color: "#9CA3AF" }}>
              No automated flow insights available for this asset.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Automated Workflow label */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem", color: "#1B2A3D" }}>
                Automated Workflow
              </Typography>
              <Chip
                label="ML Prediction"
                size="small"
                sx={{
                  height: 22,
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  bgcolor: "#EDE9FE",
                  color: "#7C3AED",
                }}
              />
            </Box>

            {/* Decision Reasoning */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#1B2A3D", mb: 1 }}>
                Complete Decision Reasoning
              </Typography>
              <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#1B2A3D", mb: 0.75 }}>
                Data Inputs Analyzed
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 1.5 }}>
                {STATIC_DATA_INPUTS.map((input, idx) => (
                  <Box
                    component="li"
                    key={idx}
                    sx={{ fontSize: "0.8125rem", color: "#4B5563", lineHeight: 1.8 }}
                  >
                    {input}
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Detailed Analysis */}
            <Box
              sx={{
                mt: 2,
                mb: 2,
                p: 2,
                bgcolor: "#F9FAFB",
                borderRadius: 1,
                border: "1px solid #E5E7EB",
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#1B2A3D", mb: 0.75 }}>
                Detailed Analysis
              </Typography>
              <Typography sx={{ fontSize: "0.8125rem", color: "#4B5563", lineHeight: 1.7 }}>
                {flowInsights?.detailedAnalysis}
              </Typography>
            </Box>

            {/* Timeline Steps 1 → 4 */}
            <Box>
              {STEP_META.map((meta, index) => (
                <StepCard
                  key={meta.stepNumber}
                  meta={meta}
                  description={getStepDescription(meta.stepNumber)}
                  isLast={index === STEP_META.length - 1}
                />
              ))}
            </Box>
          </>
        )}

      </CardContent>
    </Card>
  );
};

export default LogsAndEvents;